import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { usePathname } from "next/navigation";
import { HotelDetailURLParams, AllURLParams } from "@/lib/Types/URLParameters";
import fetchData from "@/lib/Fetchers/fetchData";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { getFilterKey } from "@/lib/Helpers/getFilterKey";
import { viewModeFromPathAndQuery } from "@/lib/Helpers/currentUrl";
import { channel } from "@/lib/Types/Channel";
import { AppConfig } from "@/lib/Types/AppConfig";
import { DATE_LIST_CHEAPEST_OFFER_QUERY } from "@/lib/Fetchers/GraphQlQueries/getDateListOffers";
import { convertMinutesToTime } from "@/lib/Helpers/dateHelpers";

interface DateListGetRequest {
  channel: string;
  cid: number;
  from: string;
  to?: string;
  duration: string;
  travellers?: string;
  rooms?: string;
  m: string;
  p: string;
  platformName: string;
  subsource?: string;
  device?: string;
  userUid?: string;
  searchId?: string;
  departureAirport?: string;
  directFlight?: boolean;
  airlineCodes?: string;
  // Price-affecting filters mirrored from the calendar / offer list so tab
  // prices stay consistent with what the user sees (e.g. an active mealType).
  mealType?: number;
  roomType?: string;
  roomAddition?: string;
  price?: string;
  tourOperator?: string;
  transfer?: boolean;
  rentalCar?: boolean;
  railAndFly?: boolean;
  categories?: string;
  flexibleOffers?: string;
  flexibleOffersExpected?: boolean;
  bookOnOption?: string;
  specialDealTypes?: string;
  departureFlightTimeFrom?: string;
  departureFlightTimeUntil?: string;
  returnFlightTimeFrom?: string;
  returnFlightTimeUntil?: string;
}

async function fetchDurationCheapestPrice(
  searchParams: HotelDetailURLParams,
  appConfig: AppConfig,
  cid: string,
  duration: string,
): Promise<number | null> {
  const { envConfig, viewMode, userData, mpt } = appConfig;

  // Voucher / special-deal price filter — mirror useDateListSearch so a tab's
  // cheapest price reflects the same discounted offer set the list shows.
  const specialDealTypes = searchParams.toVoucher
    ? "TO_VOUCHER"
    : searchParams.specialDeals
      ? "DISCOUNT_PRICE,TO_VOUCHER"
      : undefined;

  const request: DateListGetRequest = {
    channel: viewMode.channel,
    cid: Number(cid),
    // Mirror useDateListSearch exactly so a tab's cheapest price reflects the
    // same offer set the list shows: `from` follows the day picked in the price
    // calendar (`priceCurveFrom`) when present, otherwise the raw search `from`.
    // `priceCurveFrom` is always a future calendar bar, so the backend's
    // "from must be in the future" validation on datelistsearch holds.
    from: searchParams.priceCurveFrom || searchParams.from,
    duration,
    m: viewMode.mandator.toString(),
    p: viewMode.profile.toString(),
    platformName: "ICARUS",
    subsource: searchParams.bf_subsource || "S17PDV10S17PD01",

    // Mirror useDateListSearch: with priceCurveFrom set, omit `to` — the backend
    // derives the window from `from` + `duration`.
    ...(!searchParams.priceCurveFrom &&
      searchParams.to && { to: searchParams.to }),
    ...(searchParams.rooms && {
      rooms: searchParams.rooms,
      travellers: searchParams.rooms,
    }),
    ...(userData?.deviceType && { device: userData.deviceType }),
    ...(userData?.userUid && { userUid: userData.userUid }),
    ...(searchParams.searchId && { searchId: searchParams.searchId }),
    ...(searchParams.departureAirport && {
      departureAirport: searchParams.departureAirport,
    }),
    ...(searchParams.directFlight === "1" && { directFlight: true }),
    ...(searchParams.airline && { airlineCodes: searchParams.airline }),

    // Filters that affect price — keep in sync with usePriceCurve / useDateListSearch.
    ...(searchParams.mealType && { mealType: Number(searchParams.mealType) }),
    ...(searchParams.roomType && { roomType: searchParams.roomType }),
    ...(searchParams.oceanView === "true" && { roomAddition: "OCEANVIEW" }),
    ...(searchParams.price && {
      price: `0_${parseInt(searchParams.price).toFixed(0)}`,
    }),
    ...(searchParams.tourOperator && {
      tourOperator: searchParams.tourOperator,
    }),
    ...(searchParams.transfer === "1" && { transfer: true }),
    ...(searchParams.rentalCar === "1" && { rentalCar: true }),
    ...(["1", "true"].includes(searchParams.railAndFly) && { railAndFly: true }),
    // GraphQL input takes categories as a CSV String (see getProductSearchOffers
    // `$categories: String`), so forward the raw param, not a split array.
    ...(searchParams.categories && { categories: searchParams.categories }),
    ...(searchParams.flexibleOffers && {
      flexibleOffers: "flexibleCancellation",
    }),
    ...(searchParams.flexibleOffersExpected === "true" && {
      flexibleOffersExpected: true,
    }),
    ...(searchParams.bookOnOption && { bookOnOption: "true" }),
    ...(specialDealTypes && { specialDealTypes }),

    // Flight-time filters — datelist expects "HH:mm" (unlike pricecurve's raw
    // minutes). Skip the "until" bounds at 1440 (24:00 = no upper limit), same
    // guards as useDateListSearch.
    ...(searchParams.departureFlightTimeFrom && {
      departureFlightTimeFrom: convertMinutesToTime(
        searchParams.departureFlightTimeFrom,
      ),
    }),
    ...(searchParams.departureFlightTimeUntil &&
      searchParams.departureFlightTimeUntil !== "1440" && {
        departureFlightTimeUntil: convertMinutesToTime(
          searchParams.departureFlightTimeUntil,
        ),
      }),
    ...(searchParams.returnFlightTimeFrom && {
      returnFlightTimeFrom: convertMinutesToTime(
        searchParams.returnFlightTimeFrom,
      ),
    }),
    ...(searchParams.returnFlightTimeUntil &&
      searchParams.returnFlightTimeUntil !== "1440" && {
        returnFlightTimeUntil: convertMinutesToTime(
          searchParams.returnFlightTimeUntil,
        ),
      }),
  };

  const url = `${envConfig.graphqlApi}/comvel-datelistsearch-service/graphql`;

  const data = await fetchData({
    url,
    method: "POST",
    params: {
      query: DATE_LIST_CHEAPEST_OFFER_QUERY,
      variables: { request },
    },
    mpt: mpt || undefined,
    envConfig,
    service: "durationTabPrice",
    transactionId: searchParams.transactionId,
    searchId: searchParams.searchId,
    timeout: 60000,
  });

  // fetchData resolves (never throws) with `{ apiError: true }` on a
  // network/timeout/non-2xx failure. Without this guard that case falls through
  // to `!cheapestOffer` below and returns null — indistinguishable from a
  // genuine sold-out duration: the tab silently shows "—", react-query's retry
  // never fires, and nothing lands in Kibana. Mirror the sibling fetchers
  // (usePriceCurve / useDateListSearch): log a FAIL and throw so the query
  // retries and the failure is traceable.
  if (data?.apiError) {
    sendKibanaLog({
      envConfig,
      transactionId: searchParams.transactionId,
      message: "DURATION TAB PRICE FAIL",
      component: "durationTabPrice",
      jsonMessage: { message: data.message, duration, cid },
      searchId: searchParams.searchId,
      category: "debug",
    });
    throw new Error(data.message || "durationTabPrice: fetch failed");
  }

  const cheapestOffer =
    data?.data?.getDateListOffers?.cheapestOffer ?? null;
  if (!cheapestOffer) return null;

  // Match the total shown by the calendar / offer list (OfferList.tsx uses
  // cheapestOffer.estimatedTotalPrice; OfferListItem prefers totalPrice). `price`
  // is per-person, so it is only the last-resort fallback.
  return (
    cheapestOffer.totalPrice ??
    cheapestOffer.estimatedTotalPrice ??
    cheapestOffer.price ??
    null
  );
}

/**
 * Cheapest price for a single stay duration, used per duration tab. Each visible
 * tab calls this with its own duration; react-query caches per duration so
 * scrolling back to a seen tab is instant.
 *
 * `enabled` lets callers defer the (non-trivial) datelist call until the tab is
 * actually worth fetching — e.g. only once it scrolls into view — so we don't
 * fire one request per duration up front on mobile. Defaults to `true` so eager
 * callers (the active-price baseline) are unaffected.
 */
export function useDurationTabPrice(
  appConfig: AppConfig,
  duration: number,
  enabled = true,
) {
  const { envConfig, WlConfig } = appConfig;
  const searchParams = useReactiveSearchParams();
  const pathname = usePathname();

  const searchParamsObject = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params as HotelDetailURLParams;
  }, [searchParams]);

  const viewMode = viewModeFromPathAndQuery(
    pathname,
    searchParamsObject as AllURLParams,
    WlConfig.viewMode,
  );

  const cid = pathname?.match(/cid_(\d+)/)?.[1];

  // Exclude `duration` (overridden per tab via the explicit key) and result-only
  // params so the cache key only reflects price-affecting search context.
  // `priceCurveFrom` is intentionally KEPT in the key: picking a day in the
  // price calendar must refetch the cheapest price for that day.
  const filterKey = useMemo(
    () =>
      getFilterKey(searchParamsObject, [
        "duration",
        "searchId",
        "preselectedOfferId",
        "clickId",
        "sort",
      ]),
    [searchParamsObject],
  );

  const query = useQuery({
    queryKey: ["durationTabPrice", duration, cid ?? null, filterKey],
    queryFn: () =>
      fetchDurationCheapestPrice(
        searchParamsObject,
        { ...appConfig, viewMode },
        cid as string,
        duration.toString(),
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled:
      enabled &&
      !!envConfig &&
      !!viewMode &&
      viewMode.channel === channel.PACKAGE &&
      !!cid &&
      !!searchParamsObject.from &&
      duration > 0,
  });

  return {
    price: query.data ?? null,
    isLoading: query.isLoading,
  };
}
